import { lazy } from "react";
import { createRoutesFromElements, Outlet, Route } from "react-router-dom";
import { BrokerOrBorrowerRoutes } from "src/apps/BrokerOrBorrowerRoutes";
import MaintenanceIfEnabled from "src/apps/shared/Maintenance";
import { SharedRoutes } from "src/apps/shared/routes";
import { RequireAuth } from "src/components";
import { DocumentWorkspaceWindowBridge } from "src/components/DocumentWorkspace/DocumentWorkspaceWindowBridge";
import { NotificationCenter } from "src/components/NotificationCenter/NotificationCenter";
import { NotificationWsSubscription } from "src/components/NotificationCenter/NotificationWsSubscription";
import AppRoot from "src/components/root/AppRoot";
import { RouteCrashRecovery } from "src/components/root/RouteCrashRecovery";
import MissingRouteRedirect from "src/components/root/routeHelpers/MissingRouteRedirect";
import TrackRoute from "src/components/root/routeHelpers/TrackRoute";
import config from "src/config";
import { EmbedRoutes } from "src/embeds/routes";
import { useCrossWindowCacheSync } from "src/hooks/useCrossWindowCacheSync";
import { useWebsocketAuthenticated } from "src/hooks/useWebsocketAuthenticated";
import { lazyImport } from "src/lib";

const ApplyPage = lazy(() => lazyImport(() => import("src/apps/Apply")));
const PasswordProtectedFileDownload = lazy(() =>
  lazyImport(() => import("src/apps/PortfolioDashboard/screens/PasswordProtectedFileDownload"))
);

const QuickbooksCallback = lazy(() => lazyImport(() => import("src/pages/QuickbooksCallback")));
const GmailCallback = lazy(() => lazyImport(() => import("src/pages/GmailCallback")));
const DetachedWindowRoute = lazy(() =>
  lazyImport(() => import("src/components/detached/DetachedWindowRoute"))
);

const AppRouteLayout = () => {
  return (
    // @ts-expect-error
    <AppRoot>
      <Outlet />
    </AppRoot>
  );
};

const AuthRouteLayout = () => {
  // Every native window keeps its own query cache, so each one both publishes what it changes and
  // applies what the others do. Mounted here rather than in a main-window-only bridge: the deal a
  // detached workspace is showing moves under it too.
  useCrossWindowCacheSync();
  /**
   * Set up the websocket connection for the authenticated user so that it doesn't disconnect
   * in between pages.
   */
  useWebsocketAuthenticated({ routeKey: "__open__", enabled: true });
  return (
    <>
      <DocumentWorkspaceWindowBridge />
      <NotificationWsSubscription />
      <NotificationCenter />
      <Outlet />
    </>
  );
};

const Router = createRoutesFromElements([
  <Route key="app" element={<AppRouteLayout />} errorElement={<RouteCrashRecovery />}>
    {/* Borrower-facing /apply chat experience. Registered OUTSIDE the auth
        wall (same level as /application above) so anonymous borrowers can
        land on it without being bounced to /login. The borrower-session
        token is read out of localStorage on mount and used as the bearer
        for every backend request via the axios interceptor. */}
    <Route
      key="/apply"
      path="/apply"
      element={
        <TrackRoute title={`Apply | ${config.brandName}`}>
          <ApplyPage />
        </TrackRoute>
      }
    />
    <Route
      key="/apply/:applicationId"
      path="/apply/:applicationId"
      element={
        <TrackRoute title={`Apply | ${config.brandName}`}>
          <ApplyPage />
        </TrackRoute>
      }
    />
    <Route
      key="/"
      element={
        <MaintenanceIfEnabled flag="dashboard">
          <AuthRouteLayout />
        </MaintenanceIfEnabled>
      }
    >
      <Route path="/desktop-window/:surface" element={<RequireAuth />}>
        <Route index element={<DetachedWindowRoute />} />
      </Route>
      <Route path="/*" element={<RequireAuth />}>
        <Route path="*" element={<BrokerOrBorrowerRoutes />} />
      </Route>

      {SharedRoutes()}
      <Route key="/download-file" path="/download-file" element={<PasswordProtectedFileDownload />} />
      <Route key="/quickbooks/callback" path="/quickbooks/callback" element={<QuickbooksCallback />} />
      <Route key="/gmail/callback" path="/gmail/callback" element={<GmailCallback />} />
    </Route>
    {EmbedRoutes()}
  </Route>,
  <Route key="*" path="*" element={<MissingRouteRedirect />} errorElement={<RouteCrashRecovery />} />,
]);

export default Router;
